'use client';

import { useRouter, useSearchParams } from 'next/navigation';

/**
 * Custom hook to redirect to a new path while preserving the current page's query parameters.
 *
 * Example:
 * - Current URL: /test?query1=value1
 * - Calling redirectWithQuery('/home?myQuery=test') results in:
 *   /home?myQuery=test&query1=value1
 *
 * pathname The target path, optionally with its own query string.
 * shouldKeepQuery Whether to preserve current page query parameters (default: true).
 */
export function useRedirectWithQuery() {
  const router = useRouter();
  const searchParams = useSearchParams();

  const redirectWithQuery = (
    pathname: string,
    shouldKeepQuery: boolean = true
  ) => {
    const currentParams = new URLSearchParams(
      shouldKeepQuery ? searchParams?.toString() : ''
    );

    const [basePath, existingQuery] = pathname.split('?');
    const targetParams = new URLSearchParams(existingQuery || '');

    // Merge only if not already present
    currentParams.forEach((value, key) => {
      if (!targetParams.has(key)) {
        targetParams.append(key, value);
      }
    });

    const finalUrl =
      targetParams.toString().length > 0
        ? `${basePath}?${targetParams.toString()}`
        : basePath;

    router.push(finalUrl);
  };

  return redirectWithQuery;
}
